<template>
  <div class="gs-checkout-builder-step gs-checkout-price-points-step">
    <div class="gs-checkout-price-points-step__layout" :class="{ 'has-preview': showRemoveCart }">
      <section class="gs-checkout-price-points-step__list-card">
        <div v-if="pricepoint_err" class="gs-checkout-price-points-step__error" role="alert">
          {{ $t('frontend_price_poit_must_enable') }}
        </div>
        <div v-if="pricePoints.length" class="gs-checkout-price-points-step__master-row">
          <div>
            <strong>{{ $t('frontend_checkout_links_step3_enable_all_price_points') }}</strong>
            <span>Make every available offer selectable from this checkout.</span>
          </div>
          <gd-toggle
            v-model="enable_all_price_point"
            :value="enable_all_price_point"
            :aria-label="$t('frontend_checkout_links_step3_enable_all_price_points')"
            @change="changeEnableAllPricePoint($event)"
          />
        </div>

        <p v-if="pricePoints.length > 1" class="gs-checkout-price-points-step__hint">
          Drag to set the order customers see on the checkout.
        </p>

        <draggable
          v-if="pricePoints.length"
          :list="pricePoints"
          item-key="id"
          tag="div"
          class="gs-checkout-price-points-step__list"
          handle=".gs-checkout-price-points-step__handle"
          :animation="150"
          ghost-class="is-ghost"
          @change="handlePricePointReorder"
        >
          <template v-for="(pricePoint, pricePointIndex) in pricePoints">
          <li
            :key="pricePoint.id"
            :class="{
              'is-disabled': !pricePoint.enabled,
              'is-hidden-divider-row': pricePointIndex === firstHiddenPricePointIndex,
            }"
          >
            <div v-if="pricePointIndex === firstHiddenPricePointIndex" class="gs-checkout-price-points-step__hidden-divider">
              <button type="button" @click="showHiddenPricePoints = !showHiddenPricePoints" :aria-expanded="showHiddenPricePoints ? 'true' : 'false'">
                <gs-icon :name="showHiddenPricePoints ? 'chevron-up' : 'chevron-down'" size="16"></gs-icon>
                <span>Hidden price points ({{ hiddenPricePointCount }})</span>
              </button>
              <small>These offers remain available, but are kept out of the main list.</small>
            </div>
            <div v-if="isMainPricePoint(pricePoint) || showHiddenPricePoints" class="gs-checkout-price-points-step__item-row">
            <button
              type="button"
              class="gs-checkout-price-points-step__handle"
              :aria-label="`Drag to reorder ${pricePoint.name}`"
              title="Drag to reorder"
            >
              <gs-icon name="grip-vertical" size="16"></gs-icon>
            </button>
            <div class="gs-checkout-price-points-step__row-copy">
                <h3>
                  <span v-if="pricePoint.internal_name">
                    {{ pricePoint.internal_name }}
                    <small>{{ pricePoint.name }}</small>
                  </span>
                  <span v-else>
                    {{ pricePoint.name }}
                  </span>
                </h3>
                <p v-if="pricePoint.description">{{ pricePoint.description }}</p>
                <span class="gs-checkout-price-points-step__price">
                  <span v-if="pricePoint.number_of_installments > 0">
                    {{ pricePoint.number_of_installments }}×
                  </span>
                  {{ localModel.currency_symbol }}{{ pricePoint.price }}</span>
                <span v-if="pricePoint.recurring_interval === 1" class="gs-checkout-price-points-step__interval">
                  {{ $t('frontend_product_weekly') }}
                </span>
                <span v-else-if="pricePoint.recurring_interval === 2" class="gs-checkout-price-points-step__interval">
                  {{ $t('frontend_every_2_weeks') }}
                </span>
                <span v-else-if="pricePoint.recurring_interval === 3" class="gs-checkout-price-points-step__interval">
                  {{ $t('frontend_product_monthly') }}
                </span>
                <span v-else-if="pricePoint.recurring_interval === 4" class="gs-checkout-price-points-step__interval">
                  {{ $t('frontend_every_2_months') }}
                </span>
                <span v-else-if="pricePoint.recurring_interval === 5" class="gs-checkout-price-points-step__interval">
                  {{ $t('frontend_quarterly') }}
                </span>
                <span v-else-if="pricePoint.recurring_interval === 9" class="gs-checkout-price-points-step__interval">
                  {{ $t('frontend_every_4_months') }}
                </span>
                <span v-else-if="pricePoint.recurring_interval === 6" class="gs-checkout-price-points-step__interval">
                  {{ $t('frontend_half_yearly') }}
                </span>
                <span v-else-if="pricePoint.recurring_interval === 7" class="gs-checkout-price-points-step__interval">
                  {{ $t('frontend_product_yearly') }}
                </span>
                <span v-else-if="pricePoint.recurring_interval === 8" class="gs-checkout-price-points-step__interval">
                  {{ $t('frontend_product_every_2_years') }}
                </span>
            </div>
              <gd-toggle
                v-model="pricePoint.enabled"
                :value="pricePoint.enabled"
                :aria-label="`Include ${pricePoint.name} in checkout`"
                @change="pricePointChange"
              />
            </div>
          </li>
          </template>
        </draggable>

        <div v-if="!pricePoints.length" class="gs-checkout-price-points-step__empty">
          No price points are available for this product.
        </div>

        <p v-if="showNote" class="gs-checkout-price-points-step__note">
          <strong>{{ $t('frontend_note') }}:</strong>
          {{ $t('frontend_checkout_links_step3_click_upsell_note') }}
        </p>
      </section>
      <aside v-if="showRemoveCart" class="gs-checkout-price-points-step__preview">
        <div class="gs-checkout-price-points-step__preview-copy">
          <strong>{{ $t('frontend_single_price_display') }}</strong>
          <span>Preview of the buyer price-confirmation state.</span>
        </div>
        <div class="gs-checkout-price-points-step__preview-image">
          <img v-if="localModel.customization_details.customization_details.show_price_confirmation"
            src="@/assets/images/checkout/PriceSelectOn.png" alt="Checkout with price confirmation enabled" />
          <img v-else src="@/assets/images/checkout/PriceSelectOff.png" alt="Checkout with price confirmation disabled" />
        </div>
        <div class="gs-checkout-price-points-step__preview-toggle">
          <label>{{ $t('frontend_show_price_confirmation') }}</label>
          <gd-toggle
            v-model="localModel.customization_details.customization_details.show_price_confirmation"
            :aria-label="$t('frontend_show_price_confirmation')"
          />
        </div>
      </aside>
    </div>
    <popup-alert v-if="alertProps.show" :variant="alertProps.variant" @close="alertProps.show = false">
      <template v-slot:title>
        {{ alertProps.title }}
      </template>
      <p>{{ alertProps.message }}</p>
    </popup-alert>
  </div>
</template>

<script>
import draggable from 'vuedraggable';

export default {
  name: 'PricePointsStep',
  components: {
    draggable,
  },
  props: {
    pricePoints: {
      required: true,
    },
    product: {
      required: true,
    },
    model: {
      required: true,
    },
  },

  data() {
    return {
      alertProps: {
        show: false,
        variant: 'success',
        title: '',
        message: '',
      },
      localModel: this.model,
      pricepoint_err: false,
      enable_all_price_point: true,
      showHiddenPricePoints: false,
    };
  },

  async created() {

    this.pricePoints.map((value, index) => {
      if (!value.enabled) {
        this.enable_all_price_point = false;
      }
    });
  },

  computed: {
    mainPricePointCount() {
      return this.pricePoints.filter(this.isMainPricePoint).length;
    },
    hiddenPricePointCount() {
      return this.pricePoints.length - this.mainPricePointCount;
    },
    firstHiddenPricePointIndex() {
      const index = this.pricePoints.findIndex((pricePoint) => !this.isMainPricePoint(pricePoint));
      return index === -1 ? null : index;
    },
    showNote() {
      if (this.product.checkout_page_options.express_checkout > 0) {
        const enabled = this.pricePoints.filter((value, index) => {
          return value.enabled == true;
        });
        return enabled.length > 1
      }
      return false;

    },

    showRemoveCart() {
      const allEnabled = this.pricePoints.filter((value) => {
        return value.enabled == true;
      });
      return allEnabled.length === 1;
    },
  },

  methods: {
    isMainPricePoint(pricePoint) {
      return pricePoint.is_pinned === undefined || pricePoint.is_pinned === null
        ? true
        : pricePoint.is_pinned === true || Number(pricePoint.is_pinned) === 1;
    },
    handlePricePointReorder() {
      // vuedraggable mutates the list in place. Emit a fresh array so the
      // checkout wizard keeps the dropped order when the parent re-renders.
      this.$emit('updatePricePoints', this.pricePoints.slice());
    },
    pricePointChange(value) {
      const allEnabled = this.pricePoints.filter((value, index) => {
        return value.enabled == false;
      });
      this.enable_all_price_point = allEnabled.length > 0 ? false : true;
    },

    changeEnableAllPricePoint(event) {
      this.pricePoints.map((value, index) => {
        return (value.enabled = event);
      });
    },

    handleDesignTabChange(newTab) {
      this.designTab = newTab;
    },

    validate() {
      const isValid = true;
      const enabledArr = [];
      this.pricePoints.map((value, index) => {
        if (value.enabled) {
          enabledArr.push(value.id);
        }
      });

      if (enabledArr.length == 0) {
        this.alertProps = {
          show: true,
          variant: 'error',
          title: 'Oops...',
          message: this.$t('frontend_price_poit_must_enable'),
        };
        return;
      }

      this.$emit('on-validate', isValid);
      return isValid;
    },
  },
};
</script>

<style scoped>
.gs-checkout-price-points-step__layout {
  display: grid;
  gap: 18px;
}

.gs-checkout-price-points-step__layout.has-preview {
  grid-template-columns: minmax(0, 1fr) minmax(280px, .62fr);
}

.gs-checkout-price-points-step__list-card,
.gs-checkout-price-points-step__preview {
  min-width: 0;
  padding: 20px;
  background: #fff;
  border: 1px solid var(--gs-border, #e2e8f0);
  border-radius: 12px;
}

.gs-checkout-price-points-step__master-row,
.gs-checkout-price-points-step__preview-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.gs-checkout-price-points-step__master-row strong,
.gs-checkout-price-points-step__master-row span,
.gs-checkout-price-points-step__preview-copy strong,
.gs-checkout-price-points-step__preview-copy span {
  display: block;
}

.gs-checkout-price-points-step__master-row strong,
.gs-checkout-price-points-step__preview-copy strong,
.gs-checkout-price-points-step__preview-toggle label {
  color: var(--gs-foreground, #182230);
  font-size: 13px;
  font-weight: 800;
}

.gs-checkout-price-points-step__master-row span,
.gs-checkout-price-points-step__preview-copy span {
  margin-top: 3px;
  color: var(--gs-muted-foreground, #667085);
  font-size: 11px;
  line-height: 1.45;
}

.gs-checkout-price-points-step__hint {
  margin: 16px 0 8px;
  color: var(--gs-muted-foreground, #667085);
  font-size: 11px;
}

.gs-checkout-price-points-step__hint + .gs-checkout-price-points-step__list {
  margin-top: 0;
}

.gs-checkout-price-points-step__list {
  display: grid;
  max-height: 380px;
  margin: 18px 0 0;
  padding: 0;
  overflow-y: auto;
  border-top: 1px solid var(--gs-border, #e2e8f0);
  list-style: none;
}

.gs-checkout-price-points-step__list li {
  padding: 0;
  border-bottom: 0;
}

.gs-checkout-price-points-step__item-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 2px;
  border-bottom: 1px solid var(--gs-border, #e2e8f0);
}

.gs-checkout-price-points-step__list li.is-hidden-divider-row {
  display: block;
}

.gs-checkout-price-points-step__list li.is-disabled .gs-checkout-price-points-step__row-copy {
  opacity: .6;
}

.gs-checkout-price-points-step__list li.is-ghost {
  opacity: .4;
  background: var(--gs-primary-soft, #fff0f7);
}

.gs-checkout-price-points-step__handle {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  color: var(--gs-muted-foreground, #667085);
  background: transparent;
  border: 0;
  border-radius: var(--gs-radius-sm, 6px);
  cursor: grab;
}

.gs-checkout-price-points-step__handle:hover {
  color: var(--gs-foreground, #182230);
  background: var(--gs-muted, #eef2f6);
}

.gs-checkout-price-points-step__handle:active {
  cursor: grabbing;
}

.gs-checkout-price-points-step__hidden-divider {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  margin: 14px 0;
  padding: 10px 14px;
  border: 1px dashed var(--gs-border-strong, #cbd5e1);
  border-radius: 10px;
  background: var(--gs-surface-subtle, #f8fafc);
}

.gs-checkout-price-points-step__hidden-divider button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--gs-foreground, #182230);
  font-size: 14px;
  font-weight: 650;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.gs-checkout-price-points-step__hidden-divider small {
  color: var(--gs-muted-foreground, #667085);
  font-size: 12px;
}

.gs-checkout-price-points-step__row-copy {
  flex: 1 1 auto;
  min-width: 0;
}

.gs-checkout-price-points-step__row-copy h3,
.gs-checkout-price-points-step__row-copy p {
  margin: 0;
}

.gs-checkout-price-points-step__row-copy h3 {
  color: var(--gs-foreground, #182230);
  font-size: 13px;
  font-weight: 800;
}

.gs-checkout-price-points-step__row-copy h3 small {
  display: block;
  margin-top: 2px;
  color: var(--gs-muted-foreground, #667085);
  font-size: 11px;
  font-weight: 600;
}

.gs-checkout-price-points-step__row-copy p {
  margin-top: 4px;
  color: var(--gs-muted-foreground, #667085);
  font-size: 11px;
}

.gs-checkout-price-points-step__price {
  display: inline-block;
  margin-top: 7px;
  color: #334155;
  font-size: 12px;
  font-weight: 800;
}

.gs-checkout-price-points-step__interval {
  margin-left: 4px;
  color: var(--gs-muted-foreground, #667085);
  font-size: 11px;
}

.gs-checkout-price-points-step__note,
.gs-checkout-price-points-step__empty,
.gs-checkout-price-points-step__error {
  margin: 16px 0 0;
  padding: 11px 12px;
  color: #475569;
  font-size: 11px;
  line-height: 1.5;
  background: #f8fafc;
  border: 1px solid var(--gs-border, #e2e8f0);
  border-radius: 8px;
}

.gs-checkout-price-points-step__note strong,
.gs-checkout-price-points-step__error {
  color: var(--gs-danger, #dc2626);
}

.gs-checkout-price-points-step__preview {
  align-self: start;
}

.gs-checkout-price-points-step__preview-image {
  display: grid;
  min-height: 260px;
  margin: 16px 0;
  padding: 14px;
  overflow: hidden;
  background: #f8fafc;
  border: 1px solid var(--gs-border, #e2e8f0);
  border-radius: 10px;
  place-items: center;
}

.gs-checkout-price-points-step__preview-image img {
  width: 100%;
  max-height: 300px;
  object-fit: contain;
}
</style>
